import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ContentGrid from '../../components/ContentGrid';
import AppBar from '../../components/AppBar';
import AppFooter from '../../components/AppFooter';
import { GlobalMicroButton } from '../../components/GlobalMicroButton';
import { GlobalButton } from '../../components/GlobalButton';
import { GlobalDropdown } from '../../components/GlobalDropdown';
import type { GlobalDropdownOption } from '../../components/GlobalDropdown';
import { useToast } from '../../hooks/useToast';
import { Toast } from '../../components/Toast';
import { useTheme } from '../../context/ThemeProvider';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { auth, db } from '../../lib/firebase';
import { doc, getDoc, getDocs, collection, orderBy, query, updateDoc, deleteDoc } from 'firebase/firestore';
import type { CvCheckResult, CvCheckCriterion, ManagementVote } from './types';
import { CvCheckModal } from '../../components/CvCheckModal';
import DoubleConfirmationModal from '../../components/DoubleConfirmationModal';
import styles from './page.module.css';

/** Minimal shape for list items (from Firestore) */
interface CvCheckListItem {
  id: string;
  cvCheckId: string;
  createdAt: string;
  candidate?: { fullName: string };
  positionContext?: { jobTitle: string };
  recommendation?: { inviteToVIV: string };
}

const VOTE_OPTIONS: GlobalDropdownOption[] = [
  { value: '', label: '—' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'egal', label: 'Egal' },
];

function getCriterionStatusDisplay(
  status: string,
  t: (key: string) => string
): { icon: string; label: string; outcome: 'passed' | 'failed' | 'unknown' } {
  const s = status as CvCheckCriterion['status'];
  if (s === 'pass' || s === 'not_triggered') return { icon: '✓', label: t('cv_check_status_passed'), outcome: 'passed' };
  if (s === 'fail' || s === 'triggered') return { icon: '✗', label: t('cv_check_status_failed'), outcome: 'failed' };
  if (s === 'not_applicable') return { icon: 'n/a', label: t('cv_check_status_unknown'), outcome: 'unknown' };
  return { icon: '?', label: t('cv_check_status_unknown'), outcome: 'unknown' };
}

export default function CvCheckPage() {
  const { cvCheckId: urlCvCheckId } = useParams<{ cvCheckId?: string }>();
  const navigate = useNavigate();
  const { t } = useLocale();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { isVisible, message, type, showToast, hideToast } = useToast();

  const [showNewCheckModal, setShowNewCheckModal] = useState(false);
  const [result, setResult] = useState<CvCheckResult | null>(null);
  const [resultLoading, setResultLoading] = useState(false);
  const [resultError, setResultError] = useState<string | null>(null);

  const [manager1Vote, setManager1Vote] = useState('');
  const [manager2Vote, setManager2Vote] = useState('');
  const [isSubmittingVotes, setIsSubmittingVotes] = useState(false);

  const [checkList, setCheckList] = useState<CvCheckListItem[]>([]);
  const [checkListLoading, setCheckListLoading] = useState(true);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const effectiveId = urlCvCheckId || (result?.cvCheckId ?? null);

  const fetchCvChecks = useCallback(async () => {
    setCheckListLoading(true);
    try {
      const q = query(
        collection(db, 'cvChecks'),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      const items: CvCheckListItem[] = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          cvCheckId: d.id,
          createdAt: data.createdAt || '',
          candidate: data.candidate,
          positionContext: data.positionContext,
          recommendation: data.recommendation,
        };
      });
      setCheckList(items);
    } catch (err) {
      console.error('Failed to fetch cv checks', err);
      setCheckList([]);
    } finally {
      setCheckListLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCvChecks();
  }, [fetchCvChecks]);

  // Load result when we have an id
  useEffect(() => {
    if (!effectiveId) {
      setResult(null);
      setResultError(null);
      return;
    }
    setResultLoading(true);
    setResultError(null);
    const docRef = doc(db, 'cvChecks', effectiveId);
    getDoc(docRef)
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data() as CvCheckResult;
          setResult(data);
          const votes = data.managementVoting?.votes || [];
          setManager1Vote(votes.find((v) => v.userId === 'manager1')?.vote ?? '');
          setManager2Vote(votes.find((v) => v.userId === 'manager2')?.vote ?? '');
        } else {
          setResult(null);
          setResultError('Check not found.');
        }
      })
      .catch((err) => {
        setResult(null);
        setResultError(err?.message || 'Failed to load result.');
      })
      .finally(() => setResultLoading(false));
  }, [effectiveId]);

  const handleNewCheckSuccess = useCallback(
    (cvCheckId: string) => {
      fetchCvChecks();
      navigate(`/cv-check/${cvCheckId}`);
    },
    [fetchCvChecks, navigate]
  );

  const handleDeleteCheck = useCallback(async () => {
    if (!effectiveId) return;
    setIsDeleting(true);
    try {
      await deleteDoc(doc(db, 'cvChecks', effectiveId));
      setShowDeleteModal(false);
      fetchCvChecks();
      navigate('/cv-check');
      showToast(t('cv_check_toast_deleted'), 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : t('cv_check_toast_delete_error'), 'error');
    } finally {
      setIsDeleting(false);
    }
  }, [effectiveId, showToast, navigate, fetchCvChecks, t]);

  const submitVotes = async () => {
    if (!effectiveId || (!manager1Vote && !manager2Vote)) {
      showToast(t('cv_check_toast_add_vote'), 'error');
      return;
    }
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    setIsSubmittingVotes(true);
    try {
      const docRef = doc(db, 'cvChecks', effectiveId);
      const snap = await getDoc(docRef);
      if (!snap.exists()) throw new Error('Check not found.');
      const now = new Date().toISOString();
      const votes: ManagementVote[] = [];
      if (manager1Vote) votes.push({ userId: 'manager1', vote: manager1Vote as 'yes' | 'no' | 'egal', createdAt: now });
      if (manager2Vote) votes.push({ userId: 'manager2', vote: manager2Vote as 'yes' | 'no' | 'egal', createdAt: now });

      let finalDecision: 'pending' | 'yes' | 'no' | 'egal' = 'pending';
      if (votes.length >= 2) {
        const yesCount = votes.filter((v) => v.vote === 'yes').length;
        const noCount = votes.filter((v) => v.vote === 'no').length;
        const egalCount = votes.filter((v) => v.vote === 'egal').length;
        if (noCount >= 1) finalDecision = 'no';
        else if (yesCount >= 2) finalDecision = 'yes';
        else if (yesCount === 1 && egalCount === 1) finalDecision = 'yes';
        else if (egalCount >= 2) finalDecision = 'egal';
        else if (yesCount === 1) finalDecision = 'yes';
      }

      await updateDoc(docRef, {
        'managementVoting.votes': votes,
        'managementVoting.finalDecision': finalDecision,
      });
      setResult((prev) =>
        prev
          ? {
              ...prev,
              managementVoting: {
                ...prev.managementVoting,
                votes,
                finalDecision,
              },
            }
          : null
      );
      showToast(t('cv_check_toast_votes_saved'), 'success');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : t('cv_check_toast_votes_error'), 'error');
    } finally {
      setIsSubmittingVotes(false);
    }
  };

  const criteriaBySeverity = (result?.criteria || []).reduce(
    (acc, c) => {
      const key = c.severity;
      if (!acc[key]) acc[key] = [];
      acc[key].push(c);
      return acc;
    },
    {} as Record<string, CvCheckCriterion[]>
  );

  const criteriaSummary = (result?.criteria || []).reduce(
    (acc, c) => {
      const s = c.status;
      if (s === 'pass' || s === 'not_triggered') acc.passed += 1;
      else if (s === 'fail' || s === 'triggered') acc.failed += 1;
      else acc.unknown += 1;
      return acc;
    },
    { passed: 0, failed: 0, unknown: 0 }
  );

  return (
    <div>
      <AppBar />
      <ContentGrid rows="64px 1fr 48px">
        <div className={styles.homeToolbarLeft}>
          <div className={styles.backButton}>
            <GlobalMicroButton
              title={t('global_back')}
              icon={
                <img
                  src={isDarkMode ? '/assets/icons/chevron.left.svg' : '/assets/icons/chevron.left.black.svg'}
                  alt="Back"
                  width={8}
                  height={8}
                />
              }
              onClick={() => navigate('/home')}
            />
          </div>
          <b>{t('cv_check_title')}</b>
        </div>
        <div className={styles.homeToolbarRight}>
          <GlobalMicroButton
            title={t('cv_check_new_check')}
            icon={
              <img
                src={isDarkMode ? '/assets/icons/plus.circle.svg' : '/assets/icons/plus.circle.black.svg'}
                alt=""
                width={16}
                height={16}
              />
            }
            onClick={() => setShowNewCheckModal(true)}
          >
            {t('cv_check_new_check')}
          </GlobalMicroButton>
        </div>

        {/* Left: list of checks (no buttons on list) */}
        <div className={styles.checkList}>
          {checkListLoading ? (
            <div className={styles.loadingBlock}>{t('cv_check_loading')}</div>
          ) : checkList.length === 0 ? (
            <p className={styles.checkListEmpty}>{t('cv_check_list_empty')}</p>
          ) : (
            <ul className={styles.checkListUl}>
              {checkList.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`${styles.checkListItem} ${effectiveId === item.cvCheckId ? styles.checkListItemActive : ''}`}
                    onClick={() => navigate(`/cv-check/${item.cvCheckId}`)}
                  >
                    {item.recommendation?.inviteToVIV && (
                      <span className={`${styles.checkListBadge} ${styles.recommendationBadge} ${styles[item.recommendation.inviteToVIV]}`}>
                        {item.recommendation.inviteToVIV.charAt(0).toUpperCase() + item.recommendation.inviteToVIV.slice(1)}
                      </span>
                    )}
                    <span className={styles.checkListCandidate}>
                      {item.candidate?.fullName || t('cv_check_empty_placeholder')}
                    </span>
                    <span className={styles.checkListJob}>
                      {item.positionContext?.jobTitle || t('cv_check_empty_placeholder')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.gridSeparatorWrapper}>
          <div className={styles.gridSeparator} />
        </div>

        {/* Right: form (new check) or result detail — same as home’s ArchiveChat area */}
        <div className={styles.formPanel}>
          {resultLoading && (
            <div className={styles.loadingBlock}>
              {t('cv_check_loading_result')}
            </div>
          )}

          {!effectiveId && !resultLoading && (
            <div className={styles.emptyPanel}>
              <p className={styles.emptyPanelText}>{t('cv_check_select_or_create')}</p>
              <GlobalButton variant="secondary" onClick={() => setShowNewCheckModal(true)}>
                {t('cv_check_new_check_button')}
              </GlobalButton>
            </div>
          )}

          {effectiveId && !resultLoading && result && (
            <>
              <div className={styles.resultSection}>
                <h1><strong>{t('cv_check_candidate')}</strong> {result.candidate.fullName}</h1>
                <h2><strong>{t('cv_check_position')}</strong> {result.positionContext.jobTitle}</h2>

                {(['hard', 'medium', 'soft'] as const).map((severity) => {
                  const list = criteriaBySeverity[severity];
                  if (!list?.length) return null;
                  return (
                    <div key={severity} className={styles.criteriaBySeverity}>
                      <h4 className={styles.severityTitle}>{severity.charAt(0).toUpperCase() + severity.slice(1)}</h4>
                      <ul className={styles.criteriaList}>
                        {list.map((c) => {
                          const { label, outcome } = getCriterionStatusDisplay(c.status, t);
                          return (
                            <li key={c.id} className={styles.criterionItem}>
                              <div className={styles.criterionHeader}>
                                <span className={styles.criterionTitle}>
                                  <span className={styles.criterionId}>{c.id}</span>
                                  {c.title}
                                </span>
                               
                              </div>
                              <div className={`${styles.criterionStatusWrapper} ${styles[`evidence${outcome.charAt(0).toUpperCase() + outcome.slice(1)}`]}`}>
                                {c.notes && <p className={styles.criterionEvidence}>{c.notes}</p>}
                                {c.evidence?.length > 0 && (
                                  <p className={styles.criterionEvidence}>
                                    {c.evidence.slice(0, 2).join(' · ')}
                                  </p>
                                )}
                                 <span className={`${styles.criterionStatus} ${styles[c.status] || styles.unknown}`}>
                                 {label}
                                </span>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}

                <h4 className={styles.severityTitle}>{t('cv_check_summary_label')}</h4>
                <div className={styles.resultCard}>
                  <h4 className={styles.sectionTitle}>{t('cv_check_summary_label')}</h4>
                  <ul className={styles.followUpList}>
                    <li>{criteriaSummary.passed} {t('cv_check_summary_passed')}</li>
                    <li>{criteriaSummary.failed} {t('cv_check_summary_failed')}</li>
                    <li>{criteriaSummary.unknown} {t('cv_check_summary_unknown')}</li>
                  </ul>
                </div>

                {result.summary.followUpQuestions.length > 0 && (
                  <div className={styles.resultCard}>
                    <h4 className={styles.sectionTitle}>{t('cv_check_follow_up_questions')}</h4>
                    <ul className={styles.followUpList}>
                      {result.summary.followUpQuestions.map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className={`${styles.recommendationCard} ${styles[`recommendationCard${(result.recommendation.inviteToVIV.charAt(0).toUpperCase() + result.recommendation.inviteToVIV.slice(1))}`]}`}>
                  <span className={`${styles.recommendationBadge} ${styles[result.recommendation.inviteToVIV]}`}>
                    {result.recommendation.inviteToVIV.charAt(0).toUpperCase() + result.recommendation.inviteToVIV.slice(1)}
                  </span>
                  <h4 className={styles.sectionTitle}>{t('cv_check_recommendation')}</h4>
                  <p className={styles.recommendationReason}>{result.recommendation.shortReason}</p>
                </div>

                <div className={styles.deleteSection}>
                  <div>
                  <GlobalButton
                    destructive
                    variant="secondary"
                    icon={<img src="/assets/icons/trash.red.svg" alt="" width={16} height={16} />}
                    iconPosition="left"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    {t('cv_check_delete_button')}
                  </GlobalButton>
                  </div>
                </div>
              </div>

              {false && (
              <div className={`${styles.resultCard} ${styles.votingSection}`}>
                <h4 className={styles.sectionTitle}>Management voting (2 required)</h4>
                <div className={styles.voteRow}>
                  <span className={styles.voteLabel}>Manager 1</span>
                  <GlobalDropdown
                    value={manager1Vote}
                    onChange={setManager1Vote}
                    options={VOTE_OPTIONS}
                    placeholder="Vote…"
                  />
                </div>
                <div className={styles.voteRow}>
                  <span className={styles.voteLabel}>Manager 2</span>
                  <GlobalDropdown
                    value={manager2Vote}
                    onChange={setManager2Vote}
                    options={VOTE_OPTIONS}
                    placeholder="Vote…"
                  />
                </div>
                <div className={styles.formActions}>
                  <GlobalButton onClick={submitVotes} isLoading={isSubmittingVotes} disabled={isSubmittingVotes}>
                    Submit votes
                  </GlobalButton>
                </div>
                {result?.managementVoting.finalDecision !== 'pending' && (
                  <p><strong>Final decision:</strong> {result?.managementVoting.finalDecision}</p>
                )}
              </div>
              )}
            </>
          )}

          {effectiveId && !resultLoading && resultError && (
            <div className={styles.loadingBlock}>
              <p>{resultError}</p>
              <GlobalButton variant="secondary" onClick={() => navigate('/cv-check')}>
                {t('cv_check_back_to_list')}
              </GlobalButton>
            </div>
          )}
        </div>

        <AppFooter inline />
      </ContentGrid>

      <CvCheckModal
        isOpen={showNewCheckModal}
        onClose={() => setShowNewCheckModal(false)}
        onSuccess={handleNewCheckSuccess}
      />

      <DoubleConfirmationModal
        isOpen={showDeleteModal}
        title={t('cv_check_delete_modal_title')}
        paragraph={t('cv_check_delete_modal_paragraph')}
        primaryLabel={t('cv_check_delete_modal_confirm')}
        primaryIcon="/assets/icons/trash.red.svg"
        secondaryLabel={t('global_cancel')}
        onPrimary={handleDeleteCheck}
        onSecondary={() => setShowDeleteModal(false)}
        primaryDestructive
        loading={isDeleting}
      />

      <Toast isVisible={isVisible} message={message || ''} type={type} onClose={hideToast} />
    </div>
  );
}
